import React, { useEffect, useState } from 'react';
import { Navigate, Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { db, functions } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';
import AnimalCard from './AnimalCard.js';
import OasisCard from './OasisCard.js';

const ADMIN_EMAIL = 'ccrane2@alumni.nd.edu';

function cardLabel(card) {
  if (!card) return '';
  return `${card.animal}${card.face === 'down' ? ' (down)' : ''}${card.orientation ? ` [${card.orientation}]` : ''}`;
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

// Plain-English description of a turn log entry, e.g. "Anna placed jerboa
// face-down in Column 1. Anna pulled jerboa from Column 2 to Column 1."
// Columns are shown 1-indexed to match how a player would actually talk
// about the board, even though oasisIndex is stored 0-indexed everywhere
// else. "Column" (not "row") since that's the up/down stack at each
// oasis -- "row" is reserved for the side-to-side arrangement of oases.
function describeMove(entry, name) {
  const sentences = [];
  for (const c of entry.placedCards ?? []) {
    const faceNote = c.face === 'down' ? ' face-down' : '';
    sentences.push(`${name} placed ${c.animal}${faceNote} in Column ${c.oasisIndex + 1}.`);
  }
  for (const c of entry.movedCards ?? []) {
    sentences.push(`${name} pulled ${c.animal} from Column ${c.fromOasisIndex + 1} to Column ${c.toOasisIndex + 1}.`);
  }
  for (const c of entry.removedCards ?? []) {
    sentences.push(`${name}'s scorpion removed ${c.animal} from Column ${c.fromOasisIndex + 1}.`);
  }
  if (entry.palmTreePlaced) {
    sentences.push(`${name} placed a palm tree in Column ${entry.palmTreePlaced.oasisIndex + 1}.`);
  }
  if (entry.roundEnded) sentences.push(entry.gameEnded ? 'The game ended.' : 'The round ended.');
  return sentences.join(' ') || `${capitalize(name)} made no board change.`;
}

// mine is forced true for every card regardless of actual side -- this is
// an admin debug view of the server's unredacted state, so tapping/
// hovering any face-down card (either player's) should reveal it, exactly
// like AnimalCard already lets you peek your own face-down cards.
function ReviewCard({ card, highlight }) {
  return (
    <div className="oasis-board-slot oasis-board-filled-slot">
      <AnimalCard card={card} mine highlight={highlight} />
    </div>
  );
}

function ReviewBoard({ state, playerNames, highlightCardIds = [] }) {
  return (
    <div className="oasis-board">
      <div className="oasis-board-row">
        {/* .oasis-board-row is a 6-column grid (deck + 5 oases) -- these
            empty placeholders keep every other row's oases aligned under
            the real board's deck column, same as OasisBoard.js. */}
        <div key="deck-spacer-opp-cards" />
        {state.oasisRow.map((oasis, i) => (
          <div
            key={`p2-${i}`}
            className="oasis-board-cards"
            // .oasis-board-cards stretches to fill its grid row (Grid's
            // default align-items) but is a flex column that otherwise
            // packs from the top -- the real board avoids ever needing
            // this by padding every column to a fixed slot count, but
            // this simpler view doesn't, so a shorter opponent column
            // needs to be pushed to the bottom explicitly to hug the
            // oasis row instead of floating at the top with a gap below.
            style={{ justifyContent: 'flex-end' }}
          >
            {/* This zone renders above the oasis row, so closest-to-oasis
                is the bottom (last rendered) -- reverse so the oldest card
                (index 0, i.e. whichever has been there longest) ends up
                there, matching the real board's convention. */}
            {[...oasis.player2Column].reverse().map(card => (
              <ReviewCard key={card.cardId} card={card} highlight={highlightCardIds.includes(card.cardId)} />
            ))}
          </div>
        ))}

        <div className="oasis-board-deck">
          {state.deck.length > 0 ? (
            <div className="animal-card">
              <div className="animal-card-back oasis-board-deck-count">{state.deck.length}</div>
            </div>
          ) : (
            <div className="oasis-board-deck-empty">
              <span className="oasis-board-deck-label">
                No cards
                <br />
                remaining
              </span>
            </div>
          )}
        </div>
        {state.oasisRow.map((oasis, i) => (
          <OasisCard
            key={`oasis-${i}`}
            oasisId={oasis.oasisId}
            palmTreeOwnerIds={oasis.palmTreeOwnerIds.map(id => (id === 'player1' ? 'me' : 'opponent'))}
          />
        ))}

        <div key="deck-spacer-p1-cards" />
        {state.oasisRow.map((oasis, i) => (
          <div key={`p1-${i}`} className="oasis-board-cards">
            {oasis.player1Column.map(card => (
              <ReviewCard key={card.cardId} card={card} highlight={highlightCardIds.includes(card.cardId)} />
            ))}
          </div>
        ))}
      </div>

      <div className="d-flex flex-wrap mt-3" style={{ gap: '1.5rem' }}>
        <div>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            {playerNames[state.player1Id] ?? 'Player 1'}'s hand
          </div>
          <div className="oasis-board-hand">
            {state.player1Hand.map(card => (
              <AnimalCard key={card.cardId} card={{ ...card, face: 'up' }} mine />
            ))}
          </div>
        </div>
        <div>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            {playerNames[state.player2Id] ?? 'Player 2'}'s hand
          </div>
          <div className="oasis-board-hand">
            {state.player2Hand.map(card => (
              <AnimalCard key={card.cardId} card={{ ...card, face: 'up' }} mine />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function columnKeyFor(side) {
  return side === 'player1' ? 'player1Column' : 'player2Column';
}

function findCardInRow(oasisRow, cardId) {
  for (let oasisIndex = 0; oasisIndex < oasisRow.length; oasisIndex++) {
    for (const side of ['player1', 'player2']) {
      const card = oasisRow[oasisIndex][columnKeyFor(side)].find(c => c.cardId === cardId);
      if (card) return { oasisIndex, side, card };
    }
  }
  return null;
}

function applyOwnEffectToRow(oasisRow, move) {
  const row = oasisRow.map(o => ({
    ...o,
    palmTreeOwnerIds: [...o.palmTreeOwnerIds],
    player1Column: [...o.player1Column],
    player2Column: [...o.player2Column],
  }));
  for (const placed of move.placedCards) {
    row[placed.oasisIndex][columnKeyFor(placed.side)].push({
      cardId: placed.cardId,
      animal: placed.animal,
      face: placed.face,
      orientation: placed.orientation,
    });
  }
  for (const mc of move.movedCards) {
    const loc = findCardInRow(row, mc.cardId);
    if (!loc) continue;
    row[loc.oasisIndex][columnKeyFor(loc.side)] = row[loc.oasisIndex][columnKeyFor(loc.side)].filter(
      c => c.cardId !== mc.cardId
    );
    row[mc.toOasisIndex][columnKeyFor(mc.toSide)].push(loc.card);
  }
  for (const rc of move.removedCards) {
    const loc = findCardInRow(row, rc.cardId);
    if (!loc) continue;
    row[loc.oasisIndex][columnKeyFor(loc.side)] = row[loc.oasisIndex][columnKeyFor(loc.side)].filter(
      c => c.cardId !== rc.cardId
    );
  }
  return row;
}

// "Anna's jerboa flipped face-up in Column 2. Jerboa pulled from Column 3
// to Column 2." preCascadeRow is the board right after the move's own
// effect but before any of this -- i.e. where each about-to-flip card
// actually was, needed to say which column it flipped in.
function describeFlipCascade(cascade, preCascadeRow, playerNames, player1Id, player2Id) {
  const ownerName = side => playerNames[side === 'player1' ? player1Id : player2Id] ?? side;
  const sentences = [];
  for (const cardId of cascade.flippedCardIds) {
    const loc = findCardInRow(preCascadeRow, cardId);
    if (!loc) continue;
    sentences.push(`${ownerName(loc.side)}'s ${loc.card.animal} flipped face-up in Column ${loc.oasisIndex + 1}.`);
  }
  for (const c of cascade.movedCards) {
    sentences.push(`${capitalize(c.animal)} pulled from Column ${c.fromOasisIndex + 1} to Column ${c.toOasisIndex + 1}.`);
  }
  for (const c of cascade.removedCards) {
    sentences.push(`Scorpion removed ${c.animal} from Column ${c.fromOasisIndex + 1}.`);
  }
  return sentences.join(' ') || 'No cards flipped.';
}

/**
 * Builds the flat, chronological sequence of things to step through: a
 * synthetic "Start" for round 1 (reconstructed -- there's no turnLog entry
 * before the first move), each real move, and -- for every move that ends
 * a round with flip-cascade data available (see functions/gameFlow.js's
 * buildTurnLogEntry, and functions/backfillFlipCascade.js for older
 * games) -- a synthetic "Flips" step describing every card that flipped
 * face-up and whatever that triggered, then a synthetic "End" (the board
 * exactly as it stood before the next round's redeal overwrote it)
 * immediately followed by a "Start" for the next round (just that same
 * move's resultingState, viewed as a deal rather than a move's effect).
 * Grouping a move's own numbered entry stays with the round it was
 * actually played in; the bookends are what carry the "start/end of round
 * N" concept, not a renumbering of real moves.
 */
function buildEntries(moves, player1Id, player2Id, playerNames) {
  const entries = [];
  const firstMove = moves[0];

  const startingHandById = new Map();
  // Only one side's pre-move-1 hand is directly known (startingHand); the
  // other side hasn't acted or drawn yet by that point, so their hand in
  // move 1's resultingState is still exactly their starting hand.
  startingHandById.set(firstMove.actingSide === 'player1' ? player1Id : player2Id, firstMove.startingHand);
  const otherSide = firstMove.actingSide === 'player1' ? 'player2' : 'player1';
  const otherId = otherSide === 'player1' ? player1Id : player2Id;
  startingHandById.set(otherId, firstMove.resultingState[`${otherSide}Hand`]);

  // Best-effort deck size before move 1: add back however many cards were
  // drawn during it (ending hand grew back to 4 from whatever was left
  // after playing, unless the deck ran out).
  const cardsDrawnInMove1 =
    firstMove.endingHand.length - (firstMove.startingHand.length - firstMove.placedCards.length);

  entries.push({
    type: 'gameStart',
    round: 1,
    state: {
      oasisRow: firstMove.resultingState.oasisRow.map(o => ({
        ...o,
        palmTreeOwnerIds: [],
        player1Column: [],
        player2Column: [],
      })),
      player1Hand: startingHandById.get(player1Id),
      player2Hand: startingHandById.get(player2Id),
      player1Id,
      player2Id,
      scores: { player1: null, player2: null },
      removedCard: firstMove.resultingState.removedCard,
      deck: Array(firstMove.resultingState.deck.length + cardsDrawnInMove1).fill(null),
      status: 'initiated',
    },
  });

  let currentRow = entries[0].state.oasisRow;
  // Tracked alongside the board -- a round-ending move's resultingState is
  // already the *next* round's fresh deal (redeal happens as part of the
  // same move), so it's the wrong thing to show for the move entry itself
  // or to feed the flip step's "which column" lookups. This reconstructs
  // what the board/hands/deck actually looked like right after just that
  // move's own effect, before any of the round-end resolution.
  let currentHands = { player1: entries[0].state.player1Hand, player2: entries[0].state.player2Hand };
  let currentRemovedCard = entries[0].state.removedCard;
  let currentDeckLength = entries[0].state.deck.length;

  moves.forEach((m, i) => {
    const cardsDrawn = m.endingHand.length - (m.startingHand.length - m.placedCards.length);
    const handsAfterOwnEffect = { ...currentHands, [m.actingSide]: m.endingHand };
    const deckLengthAfterOwnEffect = Math.max(0, currentDeckLength - cardsDrawn);

    if (m.roundEnded) {
      // Build the move's own resulting state client-side instead of using
      // m.resultingState (see above) -- same shape ReviewBoard/the info
      // line already expect.
      const ownEffectRow = applyOwnEffectToRow(currentRow, m);
      const ownEffectState = {
        oasisRow: ownEffectRow,
        player1Hand: handsAfterOwnEffect.player1,
        player2Hand: handsAfterOwnEffect.player2,
        player1Id,
        player2Id,
        scores: m.resultingState.scores,
        removedCard: currentRemovedCard,
        deck: Array(deckLengthAfterOwnEffect).fill(null),
        status: 'in_progress',
      };
      entries.push({ type: 'move', round: m.round, move: m, i, state: ownEffectState });

      if (m.flipCascade && m.roundEndSnapshot) {
        entries.push({
          type: 'flips',
          round: m.round,
          description: describeFlipCascade(m.flipCascade, ownEffectRow, playerNames, player1Id, player2Id),
          state: { ...m.roundEndSnapshot, player1Id, player2Id, status: 'round complete' },
          highlightCardIds: [...m.flipCascade.flippedCardIds, ...m.flipCascade.movedCards.map(c => c.cardId)],
        });
      }
      if (m.roundEndSnapshot) {
        entries.push({
          type: 'roundEnd',
          round: m.roundEndSnapshot.round,
          state: { ...m.roundEndSnapshot, player1Id, player2Id, status: 'round complete' },
        });
      }
      if (!m.gameEnded) {
        entries.push({ type: 'roundStart', round: m.round + 1, state: m.resultingState });
      }

      currentRow = m.resultingState.oasisRow;
      currentHands = { player1: m.resultingState.player1Hand, player2: m.resultingState.player2Hand };
      currentRemovedCard = m.resultingState.removedCard;
      currentDeckLength = m.resultingState.deck.length;
    } else {
      entries.push({ type: 'move', round: m.round, move: m, i });
      currentRow = m.resultingState.oasisRow;
      currentHands = handsAfterOwnEffect;
      currentDeckLength = deckLengthAfterOwnEffect;
    }
  });

  return entries;
}

/**
 * Admin-only tool to step through one game's turnLog (see functions/
 * gameFlow.js's applyMove/turnLogEntry) move by move -- each entry carries
 * a full snapshot of the resulting state, so this needs no replay/
 * reconstruction logic, just rendering. Tools (prev/next/jump/flag) sit
 * above the board so they stay reachable while scanning a long game.
 */
export default function GameReviewDetail() {
  const { gameId } = useParams();
  const { currentUser, authLoaded } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moves, setMoves] = useState(null);
  const [playerNames, setPlayerNames] = useState({});
  const [index, setIndex] = useState(0);
  const [flagged, setFlagged] = useState(() => new Set());
  const [showBackfillConfirm, setShowBackfillConfirm] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillError, setBackfillError] = useState(null);

  async function loadTurnLog() {
    setLoading(true);
    setError(null);
    setMoves(null);
    setFlagged(new Set());
    try {
      const snap = await getDocs(query(collection(db, 'onlineGames', gameId, 'turnLog'), orderBy('order', 'asc')));
      if (snap.empty) {
        setError('No turn log entries found for this game (every move on it predates the turn log feature).');
        setLoading(false);
        return;
      }
      const loadedMoves = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMoves(loadedMoves);
      setIndex(loadedMoves.length); // the last real move's index in the combined entries list, see below

      const playerIds = [...new Set(loadedMoves.map(m => m.actingPlayerId))];
      const names = {};
      await Promise.all(
        playerIds.map(async id => {
          const pSnap = await getDoc(doc(db, 'players', id));
          names[id] = pSnap.exists() ? pSnap.data().name : id;
        })
      );
      setPlayerNames(names);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!currentUser || currentUser.email !== ADMIN_EMAIL) return;
    loadTurnLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, gameId]);

  if (!authLoaded) return <p>Loading…</p>;
  if (currentUser?.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;

  async function handleBackfill() {
    setShowBackfillConfirm(false);
    setBackfilling(true);
    setBackfillError(null);
    setBackfillResult(null);
    try {
      const backfillGameFlipCascade = httpsCallable(functions, 'backfillGameFlipCascade');
      const result = await backfillGameFlipCascade({ gameId });
      setBackfillResult(result.data);
      await loadTurnLog();
    } catch (err) {
      setBackfillError(err.message);
    } finally {
      setBackfilling(false);
    }
  }

  function toggleFlag(i) {
    setFlagged(current => {
      const next = new Set(current);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const entries = moves
    ? buildEntries(moves, moves[0].resultingState.player1Id, moves[0].resultingState.player2Id, playerNames)
    : null;
  const entry = entries?.[index];
  // Counts real moves only -- Start/Flips/End bookends don't count as a
  // "move" for this purpose. On a bookend, this is how many moves have
  // happened up through that point (e.g. "5 of 12" while looking at "End
  // of round 1" right after the 5th move).
  const moveNumber =
    entry?.type === 'move'
      ? entry.i + 1
      : entries?.slice(0, index).reduce((count, e) => count + (e.type === 'move' ? 1 : 0), 0);

  // Grouped for the jump-to strip: each round's entries in order (its
  // Start bookend, its real numbered moves, its End bookend).
  const roundGroups = [];
  entries?.forEach((e, i) => {
    const lastGroup = roundGroups[roundGroups.length - 1];
    if (lastGroup && lastGroup.round === e.round) {
      lastGroup.items.push({ entry: e, i });
    } else {
      roundGroups.push({ round: e.round, items: [{ entry: e, i }] });
    }
  });

  return (
    <div className="game-review-page">
      <div className="game-review-page-topbar d-flex" style={{ gap: '1rem' }}>
        <Link to="/dev" className="game-review-page-back">
          ← Dev
        </Link>
        <Link to="/dev/game-review" className="game-review-page-back">
          ← Back to Game Review
        </Link>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: '0.5rem' }}>
        <h2 className="section-title mb-0">Game Review (dev only)</h2>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={backfilling || loading}
          onClick={() => setShowBackfillConfirm(true)}
        >
          {backfilling ? 'Backfilling…' : 'Backfill flip-cascade data'}
        </Button>
      </div>

      {backfillError && <Alert variant="danger">{backfillError}</Alert>}
      {backfillResult && (
        <Alert variant="success">
          Backfilled {backfillResult.updated} move(s); {backfillResult.alreadyDone} already had exact data.
        </Alert>
      )}

      <Modal show={showBackfillConfirm} onHide={() => setShowBackfillConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Backfill flip-cascade data?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This recomputes and writes the exact split between each round-ending move's own effect and the
          round-end flip cascade for this game's turn log, for any move that doesn't already have it. It only
          adds/overwrites the <code>movedCards</code>/<code>removedCards</code>/<code>flipCascade</code>{' '}
          fields on this game's turn log entries -- nothing else about the game is touched.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowBackfillConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleBackfill}>
            Backfill this game
          </Button>
        </Modal.Footer>
      </Modal>

      {loading && <p>Loading…</p>}
      {error && <Alert variant="danger">{error}</Alert>}

      {entries && entry && (
        <>
          {/* Sticks to the top of this page's own scroll as you move
              through a long game, instead of scrolling out of reach. */}
          <div className="game-review-page-tools d-flex align-items-center flex-wrap" style={{ gap: '0.75rem' }}>
            <Button size="sm" variant="outline-primary" disabled={index === 0} onClick={() => setIndex(i => i - 1)}>
              ← Prev
            </Button>
            <span>
              Move {moveNumber} of {moves.length}
            </span>
            <Button
              size="sm"
              variant="outline-primary"
              disabled={index === entries.length - 1}
              onClick={() => setIndex(i => i + 1)}
            >
              Next →
            </Button>
            {entry.type === 'move' && (
              <Button
                size="sm"
                variant={flagged.has(entry.i) ? 'danger' : 'outline-danger'}
                onClick={() => toggleFlag(entry.i)}
              >
                {flagged.has(entry.i) ? 'Flagged as bug ✓' : 'Flag as bug'}
              </Button>
            )}
          </div>

          <div className="mb-3 d-flex flex-wrap" style={{ gap: '0.75rem' }}>
            {roundGroups.map(group => (
              <div key={group.round}>
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Round {group.round}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {(() => {
                    let pickInRound = 0;
                    return group.items.map(({ entry: e, i }) => {
                      const label =
                        e.type === 'gameStart' || e.type === 'roundStart'
                          ? 'Start'
                          : e.type === 'roundEnd'
                          ? 'End'
                          : e.type === 'flips'
                          ? 'Flips'
                          : `${e.round} - ${++pickInRound} (${e.move.order + 1})`;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setIndex(i)}
                          style={{
                            border: i === index ? '2px solid #000' : '1px solid #ccc',
                            background: e.type === 'move' && flagged.has(e.i) ? '#f8d7da' : i === index ? '#eee' : '#fff',
                            borderRadius: '0.3rem',
                            padding: '0.15rem 0.4rem',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                          }}
                          title={
                            e.type === 'move'
                              ? describeMove(e.move, playerNames[e.move.actingPlayerId] ?? e.move.actingPlayerId)
                              : e.type === 'flips'
                              ? e.description
                              : label
                          }
                        >
                          {label}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            ))}
          </div>

          {entry.type === 'move' && (
            <div className="mb-3">
              <strong>Round {entry.move.round}</strong>
              <div>
                {describeMove(entry.move, playerNames[entry.move.actingPlayerId] ?? entry.move.actingPlayerId)}
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                Starting hand: {entry.move.startingHand.map(cardLabel).join(', ') || '—'}
                <br />
                Ending hand: {entry.move.endingHand.map(cardLabel).join(', ') || '—'}
              </div>
            </div>
          )}
          {entry.type === 'flips' && (
            <div className="mb-3">
              <strong>Round {entry.round} flips</strong>
              <div>{entry.description}</div>
            </div>
          )}
          {entry.type !== 'move' && entry.type !== 'flips' && (
            <div className="mb-3">
              <strong>{entry.type === 'roundEnd' ? `End of round ${entry.round}` : `Start of round ${entry.round}`}</strong>
            </div>
          )}

          {(() => {
            // entry.state is set for a round-ending move (its own effect,
            // reconstructed -- see buildEntries; entry.move.resultingState
            // there is already the *next* round's fresh deal) and for
            // every bookend; a normal move has neither issue, so its own
            // resultingState is used directly.
            const state = entry.type === 'move' ? entry.state ?? entry.move.resultingState : entry.state;
            // Matches the live game's lastMoveCardIds: only the cards this
            // move directly placed, not ones pulled in as a side effect.
            // The flips step highlights everything the cascade itself
            // touched instead (see buildEntries).
            const highlightCardIds =
              entry.type === 'move'
                ? entry.move.placedCards.map(c => c.cardId)
                : entry.type === 'flips'
                ? entry.highlightCardIds
                : [];
            return (
              <>
                <div className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
                  Score: {state.scores.player1} to {state.scores.player2} &middot; Deck: {state.deck.length} cards
                  &middot; Status: {state.status} &middot; Burned this round: {cardLabel(state.removedCard)}
                </div>
                <ReviewBoard state={state} playerNames={playerNames} highlightCardIds={highlightCardIds} />
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
